// Minimal i18n shim for V1. The Spanish (`es`) strings below are a
// best-effort scaffold; CLAUDE.md and the focus-group #19 ask are clear
// that production Spanish requires bilingual pharmacist review by
// Dr. Marlee Grounds before user-facing release. The picker in Settings
// hides `es` behind a "Coming soon (pharmacist review)" notice until
// the LOCALE_REVIEWED flag is true for that locale.
//
// CLAUDE-RESEARCH-GAP: Wiring i18next or react-intl is the V1.1 path.
// This shim is enough for the high-traffic surfaces (greeting, hero,
// CTAs, footer) so personas can see that the path exists, but it does
// NOT translate AI chat replies or clinical flag messages. Those need
// pharmacist review per CLAUDE.md "no PERSONALIZED dose recommendations"
// and the equivalent care taken in Spanish.
// Suggested next research: i18next + ICU MessageFormat, plus bilingual
// pharmacist contract with Dr. Marlee Grounds for the clinical-engine
// string corpus.
// Priority: high. Owner: Matt + Marlee.

import { useEffect, useState } from 'react';

export type Locale = 'en' | 'es';

export const LOCALE_LABEL: Record<Locale, string> = {
  en: 'English',
  es: 'Español',
};

// Locale review gate. Flip to true ONLY after Marlee signs off on the
// translations for the surfaces below. Until then, the Settings picker
// renders `es` as disabled with a "Coming soon (pharmacist review)"
// caption.
export const LOCALE_REVIEWED: Record<Locale, boolean> = {
  en: true,
  es: false,
};

type StringKey =
  | 'home.welcome.eyebrow'
  | 'home.welcome.title'
  | 'home.welcome.cta.chat'
  | 'home.welcome.cta.snap'
  | 'home.greeting.morning'
  | 'home.greeting.afternoon'
  | 'home.greeting.evening'
  | 'common.account'
  | 'common.medicines'
  | 'common.siggy'
  | 'common.save'
  | 'common.cancel'
  | 'footer.disclaimer';

const STRINGS: Record<Locale, Record<StringKey, string>> = {
  en: {
    'home.welcome.eyebrow': 'Welcome to MedReview',
    'home.welcome.title': "Let's set up your medicine list",
    'home.welcome.cta.chat': 'Chat with Siggy',
    'home.welcome.cta.snap': 'Snap a bottle',
    'home.greeting.morning': 'Good morning',
    'home.greeting.afternoon': 'Good afternoon',
    'home.greeting.evening': 'Good evening',
    'common.account': 'Account',
    'common.medicines': 'Medicines',
    'common.siggy': 'Siggy',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'footer.disclaimer':
      'MedReview is for educational purposes and does not replace your doctor or pharmacist. In an emergency, call 911.',
  },
  es: {
    'home.welcome.eyebrow': 'Bienvenido a MedReview',
    'home.welcome.title': 'Vamos a armar tu lista de medicinas',
    'home.welcome.cta.chat': 'Hablar con Siggy',
    'home.welcome.cta.snap': 'Toma una foto del frasco',
    'home.greeting.morning': 'Buenos días',
    'home.greeting.afternoon': 'Buenas tardes',
    'home.greeting.evening': 'Buenas noches',
    'common.account': 'Cuenta',
    'common.medicines': 'Medicinas',
    'common.siggy': 'Siggy',
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'footer.disclaimer':
      'MedReview es solo informativo y no sustituye a tu doctor o farmaceutico. En una emergencia, llama al 911.',
  },
};

const COOKIE_NAME = 'medreview_locale';

function readCookieLocale(): Locale {
  if (typeof document === 'undefined') return 'en';
  const m = document.cookie.split(';').find((c) => c.trim().startsWith(`${COOKIE_NAME}=`));
  const v = m?.split('=')[1]?.trim();
  if (v === 'es' && LOCALE_REVIEWED.es) return 'es';
  return 'en';
}

function writeCookieLocale(loc: Locale): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${COOKIE_NAME}=${loc}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
}

export function useLocale(): { locale: Locale; setLocale: (l: Locale) => void } {
  const [locale, setLocaleState] = useState<Locale>('en');
  useEffect(() => {
    setLocaleState(readCookieLocale());
  }, []);
  const setLocale = (l: Locale): void => {
    if (l === 'es' && !LOCALE_REVIEWED.es) return;
    setLocaleState(l);
    writeCookieLocale(l);
    if (typeof document !== 'undefined') {
      document.documentElement.lang = l;
    }
  };
  return { locale, setLocale };
}

export function t(key: StringKey, locale: Locale = 'en'): string {
  return STRINGS[locale]?.[key] ?? STRINGS.en[key];
}
